import { PulseIndicator } from "@/components/common/pulse-indicator";
import ScrollModal from "@/components/common/scroll-modal";
import SelectField from "@/components/common/select";
import { useAuthorization } from "@/hooks/use-authorization";
import {
  useCreateEvent,
  useUpdateEvent,
  type CreateEventBody,
} from "@/queries/calendar/calendar.api";
import { Permissions } from "@/utils/permissions";
import { cn } from "@/utils/theme";
import {
  InputDateRange,
  type ValueRange,
} from "@/workflow/components/configurations/form/components/InputDateRange";
import {
  Avatar,
  AvatarFallback,
  Button,
  Heading,
  Icon,
  TextField,
  useToast,
} from "@elaniintc/goes-design-system";
import { useMemo, useState } from "react";
import type { IDaySelected } from "../..";
import { deserializationEventData } from "../utils/deserialization";
import { serializationEventData } from "../utils/serialization";
import {
  deFormatDate,
  formatFullDate,
  SelectTypeEnum,
  type EventForDay,
} from "../utils/utils";
import CustomEvent, {
  type ConfigCustomEvent,
  type SelectType,
} from "./custom-event";

interface CreateEventsModalProps {
  isOpen: boolean;
  onClose: () => void;
  handleSubmit: () => void;
  selectedDay: IDaySelected;
  eventSelected?: EventForDay;
}

export interface IFormValue {
  title: string | undefined;
  event_type: string | undefined;
  date_range: ValueRange;
  repeat_type: string | undefined;
}

const CreateEvent = ({
  isOpen,
  onClose,
  selectedDay,
  eventSelected,
  handleSubmit,
}: CreateEventsModalProps) => {
  const { hasPermission } = useAuthorization();
  const { showToast } = useToast();
  const [openCustomModal, setOpenCustomModal] = useState<boolean>(false);
  const [configCustomEvent, setConfigCustomEvent] = useState<
    ConfigCustomEvent | undefined
  >(deserializationEventData(eventSelected));
  const [formValue, setFormValue] = useState<IFormValue>({
    title: eventSelected?.name ?? undefined,
    event_type: eventSelected?.type ?? undefined, // HOLIDAY, NON_WORKING_DAY, SPECIAL_EVENT
    date_range: {
      start: eventSelected
        ? deFormatDate(eventSelected.start_date)
        : (formatFullDate(selectedDay.date) ?? ""),
      end: eventSelected
        ? deFormatDate(eventSelected.end_date ?? "")
        : (formatFullDate(selectedDay.date) ?? ""),
    },
    repeat_type:
      eventSelected?.recurrence_type === null
        ? "UNIQUE"
        : [
              SelectTypeEnum.DAILY_INTERVAL,
              SelectTypeEnum.MONTHLY_INTERVAL,
              SelectTypeEnum.WEEKLY_INTERVAL,
              SelectTypeEnum.YEARLY_INTERVAL,
            ].includes(eventSelected?.recurrence_type as unknown as SelectType)
          ? "CUSTOM"
          : (eventSelected?.recurrence_type ?? undefined),
  });

  const canDelete = hasPermission(Permissions["calendar"]["delete"]);

  const isDisabled = useMemo(() => {
    const title = String(formValue.title ?? "").trim();
    const start = String(formValue.date_range?.start ?? "").trim();
    const end = String(formValue.date_range?.end ?? "").trim();

    const missingTitle = title === "";
    const missingEventType = !formValue.event_type;
    const missingRepeatType =
      !formValue.repeat_type ||
      (formValue.repeat_type === "CUSTOM" && !configCustomEvent);
    const missingStart = start === "";
    const missingEnd = end === "";

    return (
      missingTitle ||
      missingEventType ||
      missingRepeatType ||
      missingStart ||
      missingEnd
    );
  }, [formValue, configCustomEvent]);

  const handleChange = (name: string, value: string | ValueRange) => {
    if (name === "repeat_type" && value === "CUSTOM") {
      setOpenCustomModal(true);
    }

    setFormValue((prev) => ({ ...prev, [name]: value }));
  };

  const { mutateAsync: createEvent, isPending: creating } = useCreateEvent();
  const { mutateAsync: updateEvent, isPending: updating } = useUpdateEvent();

  const isPending = creating || updating;

  const handleCreateEvent = async () => {
    const fn = eventSelected ? updateEvent : createEvent;
    const data = serializationEventData(
      formValue,
      eventSelected,
      configCustomEvent,
    );

    try {
      await fn(data as unknown as CreateEventBody);
      showToast({
        title: "Evento de calendario",
        description: "El evento fue creado correctamente",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square" type="icon">
                <Icon type="fas" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch (e) {
      const error = e as Error;
      showToast({
        title: "Error al editar registro",
        description: error.message,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    } finally {
      onClose();
    }
  };

  return (
    <>
      {openCustomModal && (
        <CustomEvent
          isOpen={openCustomModal}
          onClose={() => setOpenCustomModal(false)}
          configCustomEvent={configCustomEvent}
          handleConfigCustomEvent={(config: ConfigCustomEvent) =>
            setConfigCustomEvent(config)
          }
        />
      )}

      <ScrollModal
        isOpen={isOpen}
        onClose={onClose}
        allowScroll={false}
        modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col"
        data-testid="event-modal"
        header={
          <div className="flex items-center justify-between px-8 py-3">
            <Heading
              variant="h5"
              className="mb-0!"
              data-testid="add-event-title"
            >
              Añadir Evento
            </Heading>
            <Button
              type="button"
              onClick={onClose}
              variant="quiet"
              aria-label="Cerrar"
              className="w-8 h-8 cursor-pointer"
            >
              <Icon type="far" name="times" className="text-xl text-gray-500" />
            </Button>
          </div>
        }
        footer={
          <div
            className={cn(
              "w-full flex py-3 px-8 gap-3",
              eventSelected ? "justify-between" : "justify-end",
            )}
          >
            {eventSelected && canDelete && (
              <Button
                variant="tertiary"
                size="sm"
                type="button"
                onClick={handleSubmit}
                className="cursor-pointer text-danger"
              >
                Eliminar
              </Button>
            )}

            <div className="flex gap-2">
              <Button
                variant="tertiary"
                size="sm"
                type="button"
                onClick={onClose}
                className="cursor-pointer"
              >
                Cancelar
              </Button>
              <Button
                variant="default"
                size="sm"
                type="submit"
                className="cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
                form="new-process-form"
                disabled={isDisabled || isPending}
              >
                {eventSelected ? "Actualizar Evento" : "Añadir Evento"}
                {isPending && (
                  <Icon
                    data-testid="loading-indicator"
                    type="far"
                    name="spinner-third"
                    className={cn("text-current", "animate-spin")}
                  />
                )}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="new-process-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleCreateEvent();
          }}
          className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
        >
          <div className="flex flex-col gap-2 pb-6">
            <div>
              <label className="font-semibold text-body-loud text-sm leading-2xl">
                Título del evento <span className="text-sm text-danger">*</span>
              </label>
              <TextField
                label=""
                placeholder="Escribe el título del evento"
                name="title"
                value={formValue.title}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                disabled={isPending}
              />
            </div>

            <SelectField
              label="Tipo de Evento"
              placeholder="Selecciona un tipo de evento..."
              name="event_type"
              required
              value={formValue.event_type ?? ""}
              isDisabled={isPending}
              options={[
                {
                  value: "NON_WORKING_DAY",
                  label: "Bloqueante",
                  icon: <Icon type="far" name="calendar-xmark" />,
                  iconPosition: "left",
                },
                {
                  value: "SPECIAL_EVENT",
                  label: "Normal",
                  icon: <Icon type="far" name="calendar" />,
                  iconPosition: "left",
                },
              ]}
              onChange={(value) => {
                handleChange("event_type", value);
              }}
              isLoading={false}
            />

            <div>
              <label className="font-semibold text-body-loud text-sm leading-2xl">
                Fecha o Rango de fecha del evento{" "}
                <span className="text-sm text-danger">*</span>
              </label>
              <InputDateRange
                value={formValue.date_range}
                handleChange={handleChange}
                disabled={isPending}
              />
            </div>

            <div>
              <label className="font-semibold text-body-loud text-sm leading-2xl">
                Frecuencia de repetición{" "}
                <span className="text-sm text-danger">*</span>
              </label>

              <div className="flex gap-1 items-end">
                <div className="flex-1">
                  <SelectField
                    label=""
                    placeholder="Selecciona una frecuencia..."
                    name="repeat_type"
                    value={formValue.repeat_type ?? ""}
                    isDisabled={isPending}
                    options={[
                      {
                        value: "UNIQUE",
                        label: "Evento único",
                        icon: <Icon type="far" name="square-1" />,
                        iconPosition: "left",
                      },
                      {
                        value: "WEEKLY",
                        label: "Semanal",
                        icon: <Icon type="far" name="repeat" />,
                        iconPosition: "left",
                      },
                      {
                        value: "MONTHLY",
                        label: "Mensual",
                        icon: <Icon type="far" name="repeat" />,
                        iconPosition: "left",
                      },
                      {
                        value: "YEARLY",
                        label: "Anual",
                        icon: <Icon type="far" name="repeat" />,
                        iconPosition: "left",
                      },
                      {
                        value: "CUSTOM",
                        label: configCustomEvent
                          ? "Repetición configurada"
                          : "Configurar repetición",
                        icon: <Icon type="far" name="repeat" />,
                        iconPosition: "left",
                      },
                    ]}
                    onChange={(value) => {
                      setConfigCustomEvent(undefined);
                      handleChange("repeat_type", value);
                    }}
                    isLoading={false}
                  />
                </div>

                {formValue.repeat_type === "CUSTOM" && (
                  <Button
                    type="button"
                    size="lg"
                    aria-label="edit-repeat"
                    data-testid="edit-repeat"
                    variant="tertiary"
                    onClick={() => setOpenCustomModal(true)}
                    className="relative"
                  >
                    <Icon type="far" name="gear" className="text-current" />
                    {configCustomEvent && (
                      <PulseIndicator showIndicator={true} />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </form>
      </ScrollModal>
    </>
  );
};

export default CreateEvent;
